// src/pages/Settings.tsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Settings as SettingsIcon,
  Save,
  Building,
  Globe,
  Clock,
  LayoutGrid,
  Shield,
  CheckCircle2,
  Send,
  MessageSquare,
  RefreshCw,
  Radio,
  ExternalLink,
  AlertCircle,
  FileText,
  Percent,
  SlidersHorizontal,
  Sparkles,
  Layers,
} from 'lucide-react';
import { CategoryPricingRule } from '../types';
import { useSettings } from '../contexts/SettingsContext';
import { useToast } from '../contexts/ToastContext';
import { apiRequest } from '../lib/api';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';

export const Settings: React.FC = () => {
  const { settings, updateSettings } = useSettings();
  const { success, error: toastError } = useToast();

  const [formData, setFormData] = useState({
    app_name: settings.app_name || 'Department Management Portal',
    company_name: settings.company_name || 'Acme Global Corporation',
    timezone: settings.timezone || 'Asia/Kolkata',
    date_format: settings.date_format || 'YYYY-MM-DD HH:mm',
    default_page_size: settings.default_page_size || '25',
    session_duration_hours: settings.session_duration_hours || '12',
    min_margin_threshold_pct: settings.min_margin_threshold_pct || '15.0',
    min_margin_enforcement_mode: settings.min_margin_enforcement_mode || 'warning',
    min_margin_cost_basis: settings.min_margin_cost_basis || 'net_cost',
  });

  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      app_name: settings.app_name || prev.app_name,
      company_name: settings.company_name || prev.company_name,
      timezone: settings.timezone || prev.timezone,
      date_format: settings.date_format || prev.date_format,
      default_page_size: settings.default_page_size || prev.default_page_size,
      session_duration_hours: settings.session_duration_hours || prev.session_duration_hours,
      min_margin_threshold_pct: settings.min_margin_threshold_pct || prev.min_margin_threshold_pct,
      min_margin_enforcement_mode: settings.min_margin_enforcement_mode || prev.min_margin_enforcement_mode,
      min_margin_cost_basis: settings.min_margin_cost_basis || prev.min_margin_cost_basis,
    }));
  }, [settings]);

  const [saving, setSaving] = useState(false);

  // Telegram Integration State
  const [telegramStatus, setTelegramStatus] = useState<any>(null);
  const [loadingTelegram, setLoadingTelegram] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [registeringWebhook, setRegisteringWebhook] = useState(false);
  const [detectedChat, setDetectedChat] = useState<any>(null);
  const [detectingChat, setDetectingChat] = useState(false);
  const [telegramForm, setTelegramForm] = useState({
    bot_token: '',
    chat_id: '',
    webhook_secret: '',
    enabled: true,
  });

  const fetchDetectedChat = async () => {
    try {
      const res = await apiRequest<any>('/api/telegram/detected-chat');
      if (res?.detected) {
        setDetectedChat(res.detected);
      }
    } catch {
      // Ignored
    }
  };

  const handleManualDetectChat = async () => {
    setDetectingChat(true);
    try {
      const res = await apiRequest<any>('/api/telegram/detected-chat');
      if (res?.detected) {
        setDetectedChat(res.detected);
        setTelegramForm(prev => ({ ...prev, chat_id: res.detected.id }));
        success(`Detected chat: ${res.detected.title} (${res.detected.id})`);
      } else {
        toastError('No recent chats detected yet. Open Telegram, send /start to @geobit_price_update_bot (or add it to your group), then try again.');
      }
    } catch (err: any) {
      toastError(err.message || 'Failed to detect Telegram chat.');
    } finally {
      setDetectingChat(false);
    }
  };

  const loadTelegramStatus = async () => {
    setLoadingTelegram(true);
    try {
      const data = await apiRequest<any>('/api/telegram/status');
      if (data) {
        setTelegramStatus(data);
        setTelegramForm(prev => ({
          ...prev,
          chat_id: data.chat_id || '',
          enabled: data.enabled ?? true,
        }));
      }
      await fetchDetectedChat();
    } catch {
      // Ignored if user has no permission
    } finally {
      setLoadingTelegram(false);
    }
  };

  // Category Pricing Defaults State
  const [categoryRules, setCategoryRules] = useState<CategoryPricingRule[]>([]);
  const [selectedCatRule, setSelectedCatRule] = useState<string>('ALL');
  const [catRuleForm, setCatRuleForm] = useState({
    net_cost_pct: '23',
    traders_pct: '25',
    sub_cont_pct: '35',
    main_cont_pct: '40',
    client_pct: '50',
    min_margin_pct: '15',
    recalculate_products: false,
  });
  const [savingCatRule, setSavingCatRule] = useState(false);

  const loadCategoryRules = async () => {
    try {
      const res = await apiRequest<{ rules: CategoryPricingRule[] }>('/api/products/pricing-rules');
      if (res?.rules) {
        setCategoryRules(res.rules);
        if (selectedCatRule !== 'ALL') {
          const currentRule = res.rules.find(r => r.category === selectedCatRule) || res.rules[0];
          if (currentRule) {
            setSelectedCatRule(currentRule.category);
            setCatRuleForm({
              net_cost_pct: String(currentRule.net_cost_pct ?? 23),
              traders_pct: String(currentRule.traders_pct ?? 25),
              sub_cont_pct: String(currentRule.sub_cont_pct ?? 35),
              main_cont_pct: String(currentRule.main_cont_pct ?? 40),
              client_pct: String(currentRule.client_pct ?? 50),
              min_margin_pct: String(currentRule.min_margin_pct ?? 15),
              recalculate_products: false,
            });
          }
        }
      }
    } catch {
      // Ignored if user has no permission
    }
  };

  useEffect(() => {
    loadTelegramStatus();
    loadCategoryRules();
  }, []);

  const handleSelectCatRule = (cat: string) => {
    setSelectedCatRule(cat);
    if (cat === 'ALL') {
      const defaultRule = categoryRules[0];
      if (defaultRule) {
        setCatRuleForm(prev => ({
          ...prev,
          net_cost_pct: String(defaultRule.net_cost_pct ?? 23),
          traders_pct: String(defaultRule.traders_pct ?? 25),
          sub_cont_pct: String(defaultRule.sub_cont_pct ?? 35),
          main_cont_pct: String(defaultRule.main_cont_pct ?? 40),
          client_pct: String(defaultRule.client_pct ?? 50),
          min_margin_pct: String(defaultRule.min_margin_pct ?? 15),
          recalculate_products: false,
        }));
      }
      return;
    }
    const rule = categoryRules.find(r => r.category === cat);
    if (rule) {
      setCatRuleForm({
        net_cost_pct: String(rule.net_cost_pct ?? 23),
        traders_pct: String(rule.traders_pct ?? 25),
        sub_cont_pct: String(rule.sub_cont_pct ?? 35),
        main_cont_pct: String(rule.main_cont_pct ?? 40),
        client_pct: String(rule.client_pct ?? 50),
        min_margin_pct: String(rule.min_margin_pct ?? 15),
        recalculate_products: false,
      });
    }
  };

  const handleSaveCatRule = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingCatRule(true);
    try {
      const isAll = selectedCatRule === 'ALL' || selectedCatRule === 'all';
      const res = await apiRequest<any>('/api/products/pricing-rules', {
        method: 'PUT',
        body: JSON.stringify({
          category: selectedCatRule,
          apply_to_all_categories: isAll,
          net_cost_pct: Number(catRuleForm.net_cost_pct),
          traders_pct: Number(catRuleForm.traders_pct),
          sub_cont_pct: Number(catRuleForm.sub_cont_pct),
          main_cont_pct: Number(catRuleForm.main_cont_pct),
          client_pct: Number(catRuleForm.client_pct),
          min_margin_pct: Number(catRuleForm.min_margin_pct),
          recalculate_products: catRuleForm.recalculate_products,
        }),
      });

      success(
        catRuleForm.recalculate_products
          ? `Pricing rules saved and ${res.updatedProductsCount || 0} products recalculated successfully!`
          : `Pricing rules for ${isAll ? 'all categories' : `"${selectedCatRule}"`} updated successfully.`
      );
      loadCategoryRules();
    } catch (err: any) {
      toastError(err.message || 'Failed to update category pricing rules.');
    } finally {
      setSavingCatRule(false);
    }
  };

  const handleSaveTelegram = async (e: React.FormEvent) => {
    e.preventDefault();

    const cleanChat = telegramForm.chat_id.replace(/^@/, '').trim().toLowerCase();
    if (cleanChat === 'geobit_price_update_bot' || cleanChat === 'bot') {
      toastError('Cannot use the bot\'s own username (@geobit_price_update_bot) as Chat ID. Please enter your personal numeric Chat ID or Group ID.');
      return;
    }

    try {
      const res = await apiRequest<any>('/api/telegram/settings', {
        method: 'POST',
        body: JSON.stringify(telegramForm),
      });
      success(res.message || 'Telegram settings updated.');
      loadTelegramStatus();
    } catch (err: any) {
      toastError(err.message || 'Failed to update Telegram settings.');
    }
  };

  const handleSendTestMessage = async () => {
    const cleanChat = (telegramForm.chat_id || '').replace(/^@/, '').trim().toLowerCase();
    if (cleanChat === 'geobit_price_update_bot' || cleanChat === 'bot') {
      toastError('Cannot send a message to the bot itself (@geobit_price_update_bot). Enter your numeric Chat ID or Group ID.');
      return;
    }

    setSendingTest(true);
    try {
      const res = await apiRequest<any>('/api/telegram/test', {
        method: 'POST',
        body: JSON.stringify({ chat_id: telegramForm.chat_id || undefined }),
      });
      success(res.message || 'Test message sent to Telegram!');
    } catch (err: any) {
      toastError(err.message || 'Failed to send test message.');
    } finally {
      setSendingTest(false);
    }
  };

  const handleRegisterWebhook = async () => {
    setRegisteringWebhook(true);
    try {
      const res = await apiRequest<any>('/api/telegram/setup-webhook', {
        method: 'POST',
      });
      success(res.message || 'Webhook registered with Telegram!');
      loadTelegramStatus();
    } catch (err: any) {
      toastError(err.message || 'Failed to register webhook.');
    } finally {
      setRegisteringWebhook(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.app_name || !formData.company_name) {
      toastError('Application Name and Company Name cannot be empty.');
      return;
    }

    setSaving(true);
    try {
      await updateSettings(formData);
      success('Platform configuration saved successfully.');
    } catch (err: any) {
      toastError(err.message || 'Failed to update system settings.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-4xl space-y-6">
      <div>
        <h2 className="text-lg font-bold text-slate-900">System Settings</h2>
        <p className="text-xs text-slate-500">
          Configure global application branding, localization, and administrative defaults
        </p>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Section: Platform Branding */}
          <div>
            <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2 mb-1">
              <Building className="w-4 h-4 text-indigo-600" />
              <span>Application Branding</span>
            </h3>
            <p className="text-xs text-slate-500 mb-4">
              Customize portal branding to match your corporate identity without modifying source code.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                label="Application Name"
                required
                value={formData.app_name}
                onChange={e => setFormData(prev => ({ ...prev, app_name: e.target.value }))}
                placeholder="e.g. Department Management Portal"
                helperText="Appears on browser tabs, navigation, and sign-in screen"
              />

              <Input
                label="Company Name"
                required
                value={formData.company_name}
                onChange={e => setFormData(prev => ({ ...prev, company_name: e.target.value }))}
                placeholder="e.g. Acme Global Corporation"
                helperText="Organization legal or business entity name"
              />
            </div>
          </div>

          <div className="border-t border-slate-100 pt-6">
            <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2 mb-1">
              <Globe className="w-4 h-4 text-indigo-600" />
              <span>Timezone & Localization</span>
            </h3>
            <p className="text-xs text-slate-500 mb-4">
              Dates are stored universally in UTC and converted at display-time using this timezone setting.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Select
                label="Primary Timezone"
                value={formData.timezone}
                onChange={e => setFormData(prev => ({ ...prev, timezone: e.target.value }))}
                options={[
                  { value: 'Asia/Kolkata', label: 'Asia/Kolkata (IST - UTC+05:30)' },
                  { value: 'UTC', label: 'UTC (Coordinated Universal Time)' },
                  { value: 'America/New_York', label: 'America/New_York (EST/EDT)' },
                  { value: 'America/Los_Angeles', label: 'America/Los_Angeles (PST/PDT)' },
                  { value: 'America/Chicago', label: 'America/Chicago (CST/CDT)' },
                  { value: 'Europe/London', label: 'Europe/London (GMT/BST)' },
                  { value: 'Europe/Berlin', label: 'Europe/Berlin (CET/CEST)' },
                  { value: 'Asia/Dubai', label: 'Asia/Dubai (GST - UTC+04:00)' },
                  { value: 'Asia/Singapore', label: 'Asia/Singapore (SGT - UTC+08:00)' },
                  { value: 'Asia/Tokyo', label: 'Asia/Tokyo (JST - UTC+09:00)' },
                ]}
              />

              <Select
                label="Date & Time Format"
                value={formData.date_format}
                onChange={e => setFormData(prev => ({ ...prev, date_format: e.target.value }))}
                options={[
                  { value: 'YYYY-MM-DD HH:mm', label: '2026-09-22 14:30 (ISO Standard)' },
                  { value: 'DD/MM/YYYY HH:mm', label: '22/09/2026 14:30 (British/European)' },
                  { value: 'MM/DD/YYYY hh:mm A', label: '09/22/2026 02:30 PM (US Standard)' },
                ]}
              />
            </div>
          </div>

          <div className="border-t border-slate-100 pt-6">
            <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2 mb-1">
              <Clock className="w-4 h-4 text-indigo-600" />
              <span>Defaults & Security Timers</span>
            </h3>
            <p className="text-xs text-slate-500 mb-4">
              Configure pagination volumes and maximum inactivity session lifetimes.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Select
                label="Default Page Size"
                value={formData.default_page_size}
                onChange={e => setFormData(prev => ({ ...prev, default_page_size: e.target.value }))}
                options={[
                  { value: '10', label: '10 records per page' },
                  { value: '25', label: '25 records per page (Recommended)' },
                  { value: '50', label: '50 records per page' },
                  { value: '100', label: '100 records per page' },
                ]}
              />

              <Select
                label="Session Lifetime"
                value={formData.session_duration_hours}
                onChange={e => setFormData(prev => ({ ...prev, session_duration_hours: e.target.value }))}
                options={[
                  { value: '4', label: '4 hours' },
                  { value: '8', label: '8 hours' },
                  { value: '12', label: '12 hours (Standard business day)' },
                  { value: '24', label: '24 hours' },
                ]}
              />
            </div>
          </div>

          <div className="border-t border-slate-100 pt-6">
            <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2 mb-1">
              <Shield className="w-4 h-4 text-emerald-600" />
              <span>Minimum Margin Protection & Guardrails</span>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                Gross Margin Security
              </span>
            </h3>
            <p className="text-xs text-slate-500 mb-4">
              Enforce profitability floors across all selling tiers. Prevent pricing errors and protect minimum factory margins.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 p-4 bg-slate-50/80 rounded-xl border border-slate-200/80">
              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-700 block">
                  Minimum Gross Margin Threshold
                </label>
                <div className="relative">
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    max="90"
                    value={formData.min_margin_threshold_pct}
                    onChange={e => setFormData(prev => ({ ...prev, min_margin_threshold_pct: e.target.value }))}
                    className="w-full px-3 py-1.5 pr-8 text-sm font-semibold rounded-lg border border-slate-300 bg-white focus:ring-2 focus:ring-emerald-500"
                  />
                  <span className="absolute right-3 top-2 text-xs text-slate-400 font-bold">%</span>
                </div>
                <p className="text-[11px] text-slate-500">
                  Global floor applied if category rule doesn't specify an override.
                </p>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-700 block">
                  Enforcement Mode
                </label>
                <select
                  value={formData.min_margin_enforcement_mode}
                  onChange={e => setFormData(prev => ({ ...prev, min_margin_enforcement_mode: e.target.value as any }))}
                  className="w-full px-3 py-1.5 text-sm font-semibold rounded-lg border border-slate-300 bg-white focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="warning">Warning Only (Soft notification badge)</option>
                  <option value="strict">Strict Block (Requires explicit approval & note)</option>
                </select>
                <p className="text-[11px] text-slate-500">
                  Strict block rejects low-margin prices unless audit justification is given.
                </p>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-700 block">
                  Margin Cost Basis
                </label>
                <select
                  value={formData.min_margin_cost_basis}
                  onChange={e => setFormData(prev => ({ ...prev, min_margin_cost_basis: e.target.value as any }))}
                  className="w-full px-3 py-1.5 text-sm font-semibold rounded-lg border border-slate-300 bg-white focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="net_cost">Net Cost (Formula Cost + Internal Markup)</option>
                  <option value="cost">Base Factory Cost (Raw Unit Cost)</option>
                </select>
                <p className="text-[11px] text-slate-500">
                  Baseline cost reference used to compute gross margin percentage.
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-end pt-4 border-t border-slate-100">
            <Button type="submit" variant="primary" loading={saving} icon={<Save className="w-4 h-4" />}>
              Save Settings
            </Button>
          </div>
        </form>
      </div>

      {/* Category Pricing Rules & Markup Defaults Card */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-4 border-b border-slate-100 gap-3">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-amber-50 text-amber-600 rounded-xl">
              <SlidersHorizontal className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                <span>Category Pricing Rules & Markup Matrix</span>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-indigo-50 text-indigo-700 border border-indigo-200">
                  <Percent className="w-3 h-3 text-indigo-600" />
                  Formula Engine Active
                </span>
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">
                Configure default markup percentages for Net Cost (on Cost) and selling tiers (Traders, Contractors, Client on Net Cost).
              </p>
            </div>
          </div>

          <Link
            to="/products"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-200 text-slate-700 bg-white hover:bg-slate-50 transition shadow-xs"
          >
            <Layers className="w-3.5 h-3.5 text-indigo-600" />
            <span>Open Product Catalog</span>
          </Link>
        </div>

        <form onSubmit={handleSaveCatRule} className="space-y-5">
          {/* Category Tabs */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">Category:</span>
            <button
              type="button"
              onClick={() => handleSelectCatRule('ALL')}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition flex items-center gap-1.5 ${
                selectedCatRule === 'ALL'
                  ? 'bg-indigo-600 text-white shadow-sm'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              <span>All Products / Categories</span>
              <span className={`text-[10px] px-1.5 py-0.2 rounded-full ${selectedCatRule === 'ALL' ? 'bg-indigo-700 text-indigo-100' : 'bg-slate-200 text-slate-600'}`}>
                {categoryRules.reduce((sum, r) => sum + (r.product_count || 0), 0) || 59}
              </span>
            </button>
            {categoryRules.map(r => (
              <button
                key={r.category}
                type="button"
                onClick={() => handleSelectCatRule(r.category)}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition flex items-center gap-1.5 ${
                  selectedCatRule === r.category
                    ? 'bg-indigo-600 text-white shadow-sm'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                <span>{r.category}</span>
                {r.product_count !== undefined && (
                  <span className={`text-[10px] px-1.5 py-0.2 rounded-full ${selectedCatRule === r.category ? 'bg-indigo-700 text-indigo-100' : 'bg-slate-200 text-slate-600'}`}>
                    {r.product_count}
                  </span>
                )}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
            <div className="p-3 bg-amber-50/70 border border-amber-200 rounded-xl space-y-1">
              <label className="text-xs font-bold text-amber-900 block">
                Net Cost % <span className="text-[10px] text-amber-700 font-normal">(on Cost)</span>
              </label>
              <div className="relative">
                <input
                  type="number"
                  step="0.1"
                  required
                  value={catRuleForm.net_cost_pct}
                  onChange={e => setCatRuleForm(prev => ({ ...prev, net_cost_pct: e.target.value }))}
                  className="w-full px-2.5 py-1.5 pr-7 text-xs font-semibold rounded-lg border border-amber-300 bg-white"
                />
                <span className="absolute right-2.5 top-1.5 text-xs text-slate-500 font-bold">%</span>
              </div>
            </div>

            <div className="p-3 bg-indigo-50/70 border border-indigo-200 rounded-xl space-y-1">
              <label className="text-xs font-bold text-indigo-900 block">
                Traders % <span className="text-[10px] text-indigo-700 font-normal">(on Net)</span>
              </label>
              <div className="relative">
                <input
                  type="number"
                  step="0.1"
                  required
                  value={catRuleForm.traders_pct}
                  onChange={e => setCatRuleForm(prev => ({ ...prev, traders_pct: e.target.value }))}
                  className="w-full px-2.5 py-1.5 pr-7 text-xs font-semibold rounded-lg border border-indigo-300 bg-white"
                />
                <span className="absolute right-2.5 top-1.5 text-xs text-slate-500 font-bold">%</span>
              </div>
            </div>

            <div className="p-3 bg-indigo-50/70 border border-indigo-200 rounded-xl space-y-1">
              <label className="text-xs font-bold text-indigo-900 block">
                Sub Cont % <span className="text-[10px] text-indigo-700 font-normal">(on Net)</span>
              </label>
              <div className="relative">
                <input
                  type="number"
                  step="0.1"
                  required
                  value={catRuleForm.sub_cont_pct}
                  onChange={e => setCatRuleForm(prev => ({ ...prev, sub_cont_pct: e.target.value }))}
                  className="w-full px-2.5 py-1.5 pr-7 text-xs font-semibold rounded-lg border border-indigo-300 bg-white"
                />
                <span className="absolute right-2.5 top-1.5 text-xs text-slate-500 font-bold">%</span>
              </div>
            </div>

            <div className="p-3 bg-indigo-50/70 border border-indigo-200 rounded-xl space-y-1">
              <label className="text-xs font-bold text-indigo-900 block">
                Main Cont % <span className="text-[10px] text-indigo-700 font-normal">(on Net)</span>
              </label>
              <div className="relative">
                <input
                  type="number"
                  step="0.1"
                  required
                  value={catRuleForm.main_cont_pct}
                  onChange={e => setCatRuleForm(prev => ({ ...prev, main_cont_pct: e.target.value }))}
                  className="w-full px-2.5 py-1.5 pr-7 text-xs font-semibold rounded-lg border border-indigo-300 bg-white"
                />
                <span className="absolute right-2.5 top-1.5 text-xs text-slate-500 font-bold">%</span>
              </div>
            </div>

            <div className="p-3 bg-emerald-50/70 border border-emerald-200 rounded-xl space-y-1">
              <label className="text-xs font-bold text-emerald-900 block">
                Client % <span className="text-[10px] text-emerald-700 font-normal">(on Net)</span>
              </label>
              <div className="relative">
                <input
                  type="number"
                  step="0.1"
                  required
                  value={catRuleForm.client_pct}
                  onChange={e => setCatRuleForm(prev => ({ ...prev, client_pct: e.target.value }))}
                  className="w-full px-2.5 py-1.5 pr-7 text-xs font-semibold rounded-lg border border-emerald-300 bg-white"
                />
                <span className="absolute right-2.5 top-1.5 text-xs text-slate-500 font-bold">%</span>
              </div>
            </div>

            <div className="p-3 bg-rose-50/70 border border-rose-200 rounded-xl space-y-1">
              <label className="text-xs font-bold text-rose-900 block">
                Min Margin % <span className="text-[10px] text-rose-700 font-normal">(Floor)</span>
              </label>
              <div className="relative">
                <input
                  type="number"
                  step="0.1"
                  required
                  value={catRuleForm.min_margin_pct}
                  onChange={e => setCatRuleForm(prev => ({ ...prev, min_margin_pct: e.target.value }))}
                  className="w-full px-2.5 py-1.5 pr-7 text-xs font-semibold rounded-lg border border-rose-300 bg-white"
                />
                <span className="absolute right-2.5 top-1.5 text-xs text-slate-500 font-bold">%</span>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-2">
            <label className="flex items-center gap-2 text-xs text-indigo-950 cursor-pointer">
              <input
                type="checkbox"
                checked={catRuleForm.recalculate_products}
                onChange={e => setCatRuleForm(prev => ({ ...prev, recalculate_products: e.target.checked }))}
                className="rounded text-indigo-600 focus:ring-indigo-500 h-4 w-4"
              />
              <span>
                {selectedCatRule === 'ALL'
                  ? 'Recalculate ALL products across all categories immediately'
                  : `Recalculate all products adhering to "${selectedCatRule}" default rules immediately`}
              </span>
            </label>

            <Button type="submit" variant="primary" size="sm" loading={savingCatRule} icon={<Save className="w-3.5 h-3.5" />}>
              {selectedCatRule === 'ALL' ? 'Save Rules for All Products' : `Save Rules for ${selectedCatRule}`}
            </Button>
          </div>
        </form>
      </div>

      {/* Telegram Bot & Price Notifications Card */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-4 border-b border-slate-100 gap-3">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-sky-50 text-sky-600 rounded-xl">
              <Send className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                <span>Telegram Bot & Real-Time Alerts</span>
                {telegramStatus?.configured ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                    <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                    {telegramStatus?.bot?.username ? `@${telegramStatus.bot.username}` : 'Bot Configured'}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
                    Not Configured
                  </span>
                )}
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">
                Automated price change alerts to your company Telegram group and 2-way <code>/price</code> interactive lookup.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Link
              to="/audit-logs?tab=telegram"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-200 text-slate-700 bg-white hover:bg-slate-50 transition shadow-xs"
            >
              <FileText className="w-3.5 h-3.5 text-sky-600" />
              <span>Inquiry Logs &amp; Threads</span>
            </Link>

            <Button
              type="button"
              variant="outline"
              size="sm"
              loading={sendingTest}
              disabled={!telegramStatus?.configured}
              onClick={handleSendTestMessage}
              icon={<Send className="w-3.5 h-3.5" />}
            >
              Send Test Message
            </Button>
          </div>
        </div>

        {/* Detected Chat Banner */}
        {detectedChat && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3.5 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs">
            <div className="flex items-start sm:items-center gap-2.5 text-emerald-900">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5 sm:mt-0" />
              <div>
                <span className="font-semibold text-emerald-950">Detected Telegram Chat: </span>
                <span className="font-bold">{detectedChat.title || 'Telegram User'}</span>{' '}
                <span className="text-emerald-700">({detectedChat.type})</span> — ID:{' '}
                <code className="bg-emerald-100/80 px-1.5 py-0.5 rounded font-mono font-bold text-emerald-900">
                  {detectedChat.id}
                </code>
              </div>
            </div>
            <button
              type="button"
              onClick={() => {
                setTelegramForm(prev => ({ ...prev, chat_id: detectedChat.id }));
                success(`Applied Chat ID: ${detectedChat.id}`);
              }}
              className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-lg shadow-xs transition inline-flex items-center gap-1.5 self-start sm:self-auto"
            >
              Use This Chat ID
            </button>
          </div>
        )}

        {/* Telegram Config Form */}
        <form onSubmit={handleSaveTelegram} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label="Bot API Token"
              type="password"
              value={telegramForm.bot_token}
              onChange={e => setTelegramForm(prev => ({ ...prev, bot_token: e.target.value }))}
              placeholder={telegramStatus?.masked_token || 'e.g. 7123456789:AAF... (from @BotFather)'}
              helperText={
                telegramStatus?.configured
                  ? `Active in backend: ${telegramStatus.masked_token}. Enter a new token to override.`
                  : 'Obtain from @BotFather on Telegram or configure via TELEGRAM_BOT_TOKEN'
              }
            />

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs font-semibold text-slate-700">Broadcast Chat / Group ID</label>
                <button
                  type="button"
                  onClick={handleManualDetectChat}
                  disabled={detectingChat}
                  className="text-xs font-medium text-sky-600 hover:text-sky-700 inline-flex items-center gap-1 transition"
                >
                  <Radio className={`w-3 h-3 ${detectingChat ? 'animate-pulse text-amber-500' : ''}`} />
                  <span>{detectingChat ? 'Detecting...' : 'Auto-Detect ID'}</span>
                </button>
              </div>
              <input
                type="text"
                value={telegramForm.chat_id}
                onChange={e => setTelegramForm(prev => ({ ...prev, chat_id: e.target.value }))}
                placeholder="e.g. 123456789 or -100123456789"
                className="w-full px-3 py-2 text-sm rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 bg-white"
              />
              <p className="text-[11px] text-slate-500 mt-1">
                ⚠️ Enter your numeric Chat ID or Group ID (e.g. <code>-100...</code>). Do <strong>not</strong> enter the bot's username (<code>@geobit_price_update_bot</code>).
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center justify-between pt-2 gap-3">
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={telegramForm.enabled}
                onChange={e => setTelegramForm(prev => ({ ...prev, enabled: e.target.checked }))}
                className="w-4 h-4 rounded text-sky-600 border-slate-300 focus:ring-sky-500"
              />
              <span className="text-xs font-semibold text-slate-700">
                Enable automated price change notifications
              </span>
            </label>

            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                loading={registeringWebhook}
                disabled={!telegramStatus?.configured}
                onClick={handleRegisterWebhook}
                icon={<RefreshCw className="w-3.5 h-3.5" />}
              >
                Register Webhook
              </Button>

              <Button type="submit" variant="primary" size="sm" icon={<Save className="w-3.5 h-3.5" />}>
                Save Telegram Settings
              </Button>
            </div>
          </div>
        </form>

        {/* Telegram Instructions Box */}
        <div className="bg-sky-50/60 border border-sky-200 rounded-xl p-4 text-xs text-sky-900 space-y-3">
          <div className="font-bold flex items-center justify-between text-sky-950">
            <span className="flex items-center gap-1.5">
              <MessageSquare className="w-4 h-4 text-sky-600" />
              Quick Setup & How to Get Your Chat ID:
            </span>
            <a
              href="https://t.me/geobit_price_update_bot"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 font-semibold text-sky-700 hover:text-sky-900 underline"
            >
              <span>Open @geobit_price_update_bot</span>
              <ExternalLink className="w-3 h-3" />
            </a>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-1">
            <div className="bg-white/70 rounded-lg p-3 border border-sky-100 space-y-1">
              <div className="font-semibold text-sky-900 flex items-center gap-1">
                <span>Option A: Personal Alerts (1-on-1)</span>
              </div>
              <ol className="list-decimal list-inside space-y-1 text-sky-800">
                <li>Open <a href="https://t.me/geobit_price_update_bot" target="_blank" rel="noopener noreferrer" className="underline font-medium">@geobit_price_update_bot</a> in Telegram.</li>
                <li>Click <strong>Start</strong> or send <code>/start</code>.</li>
                <li>The bot will reply with your <strong>Chat ID</strong>.</li>
                <li>Click <strong>Auto-Detect ID</strong> above or paste it in!</li>
              </ol>
            </div>

            <div className="bg-white/70 rounded-lg p-3 border border-sky-100 space-y-1">
              <div className="font-semibold text-sky-900 flex items-center gap-1">
                <span>Option B: Company / Sales Group Alerts</span>
              </div>
              <ol className="list-decimal list-inside space-y-1 text-sky-800">
                <li>Add <code>@geobit_price_update_bot</code> to your Telegram group.</li>
                <li>Type <code>/id</code> or <code>/chatid</code> in the group.</li>
                <li>The bot posts the <strong>Group Chat ID</strong> (e.g. <code>-100...</code>).</li>
                <li>Paste the Group Chat ID above and click <strong>Send Test Message</strong>.</li>
              </ol>
            </div>
          </div>

          <div className="pt-2 border-t border-sky-200/60 text-sky-800 space-y-1">
            <p>
              <strong>2-Way Bot Commands:</strong> In Telegram, anyone can type <code>/price &lt;name or #&gt;</code> (e.g. <code>/price Betoflex 5S</code>), <code>/p &lt;name&gt;</code>, or <code>/search &lt;keyword&gt;</code> to retrieve current selling price cards on demand.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
